import type { FolderStatus, MessageKey } from '@ralph/contracts'
import { PROVIDER_LABEL, SURFACE_LABEL } from '@ralph/contracts'
import type { ServerMessages } from '@/i18n/messages/en/errors/index.ts'

/**
 * What the folders domain's refusals say, in English (the source of truth AND the type).
 *
 * The namespace is selected from `MessageKey` rather than listed by hand: a key added to
 * `FoldersPayloads` becomes a missing property HERE, in the file that owes it a sentence,
 * instead of an error at the aggregator that names no domain.
 *
 * Provider and surface names come from `PROVIDER_LABEL` / `SURFACE_LABEL` in the contract,
 * because they are proper nouns: Microsoft, OneDrive and Amazon S3 are spelled the same in
 * every language, and the one place that decides how they are spelled should stay one place.
 */
type FoldersMessages = Pick<ServerMessages, Extract<MessageKey, `folders.${string}`>>

/**
 * Where a folder stands, in the words the connected-folders tab already uses for the same
 * five states — "Watching", "Not started", "Paused", "Needs signing in again", "Last check
 * failed" — bent into a clause so a refusal can say it mid-sentence.
 *
 * A map over the whole enum, not over the states that can actually reach a refusal: a sixth
 * status has to be given words here before it compiles, which is what keeps `needs_reauth`
 * off a screen (LOC-018).
 */
const standing: Record<FolderStatus, string> = {
  previewing: 'not started yet',
  active: 'watching',
  paused: 'paused',
  needs_reauth: 'waiting to be signed in again',
  error: 'stopped after a failed check'
}

export const folders: FoldersMessages = {
  // --- the folder itself ----------------------------------------------------
  'folders.not_found': () => 'That connected folder does not exist.',
  'folders.provider_surface_mismatch': ({ provider, surface }) =>
    `A ${PROVIDER_LABEL[provider]} account cannot watch a ${SURFACE_LABEL[surface]} folder.`,
  'folders.cannot_start_in_status': ({ status }) => `This folder is ${standing[status]}, so there is nothing to start.`,
  'folders.not_watching': ({ status }) => `This folder is ${standing[status]}, so there is nothing to pause.`,
  'folders.cannot_resume_in_status': ({ status }) =>
    `This folder is ${standing[status]}, so there is nothing to resume.`,
  'folders.unknown_cadence': () => 'Choose one of the frequencies offered.',
  'folders.provider_must_match': () => 'A folder can only move to an account with the same provider.',
  'folders.account_cannot_open_folder': () => 'That account cannot open this folder.',
  'folders.file_not_in_folder': () => 'That file is not in a connected folder.',
  'folders.page_expired': () => 'That page has expired. Open the folder again.',

  // --- who will be able to search what a folder loads -----------------------
  'folders.acknowledge_everyone': () =>
    'This knowledge base is open to everyone who signs in. Acknowledge that everything included here will be searchable by all of them.',
  'folders.acknowledge_audience': () => 'Acknowledge who will be able to search what this folder loads.',
  'folders.audience_widened': () =>
    'More people can use this knowledge base than when this folder was confirmed. Confirm who can search what it loads, then check again.',

  // --- the account a folder reads through -----------------------------------
  'folders.account_not_connected': () => 'That account is not connected here.',
  'folders.account_disconnected': () => 'That account has been disconnected.',
  'folders.account_disconnected_reconnect': () => 'That account was disconnected. Connect it again as a new account.',
  'folders.account_already_disconnected': () => 'That account is already disconnected.',
  'folders.folder_account_disconnected': () =>
    'The account this folder used was disconnected. Choose another account for it first.',
  'folders.account_is_someone_elses': () =>
    'That account is somebody else’s sign-in. Only they, or an administrator, can read through it.',
  'folders.company_account_admin_only': () =>
    'That is a company account. Only an administrator can connect folders through it.',
  'folders.only_administrators_add_company_accounts': () => 'Only an administrator can add a company account.',
  'folders.only_owner_or_administrator_renews': () =>
    'Only the person who connected this account, or an administrator, can sign it in again.',
  'folders.only_owner_or_administrator_removes': () =>
    'Only the person who connected this account, or an administrator, can remove it.',
  'folders.account_cannot_be_renewed': () => 'That account cannot be renewed with this sign-in.',

  // --- signing in with a provider -------------------------------------------
  'folders.sign_in_not_configured': ({ provider }) =>
    `${PROVIDER_LABEL[provider]} sign-in is not set up on this deployment.`,
  // "Already completed" and "took too long" are `auth.sign_in_not_pending` and
  // `auth.sign_in_expired`. What stays here names a provider, which auth's cannot.
  'folders.sign_in_other_browser': () =>
    'This sign-in was started in a different browser. Start it again from the folder tab.',
  'folders.sign_in_refused_by_provider': ({ provider }) => `${PROVIDER_LABEL[provider]} did not complete the sign-in.`,
  'folders.sign_in_no_code': () => 'The sign-in came back without an authorization code.',
  'folders.sign_in_not_renewable_google': () =>
    'Google did not issue a renewable sign-in. Remove this platform from your Google account’s connected apps and sign in again.',
  'folders.sign_in_not_renewable_microsoft': () =>
    'Microsoft did not issue a renewable sign-in. Check that the app registration allows offline_access.',
  'folders.signed_in_as_other_account': ({ signedInAs, expected }) =>
    `That sign-in was ${signedInAs}, but this account is ${expected}. Sign in as ${expected} to renew it, or connect ${signedInAs} as a new account.`,

  // --- credentials this platform refuses to keep ----------------------------
  'folders.scope_can_write': ({ scopes }) =>
    `The provider granted permission to write (${scopes}). This platform only reads, and will not keep a credential that can write. Fix the app registration or the consent, then sign in again.`,
  'folders.tenant_consent_can_write': ({ scopes }) =>
    `The tenant consented to application permissions that can write (${scopes}). This platform only reads. Remove them from the app registration and connect again.`,
  'folders.s3_endpoint_not_allowed': ({ host }) =>
    `${host} is not an S3 endpoint this deployment is allowed to reach. An operator adds it to CONNECTOR_S3_ENDPOINT_HOSTS.`
}
